
import { defineComponent, PropType, ref } from "vue";
import ApiService from "@/core/services/ApiService";
import Messages from "@/core/helpers/Messages";

type ActionButton = {
  key?: string;
  label: string;
  endpoint: string | ((values: Record<string, any>, ctx: { id?: string | number }) => string);
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: Record<string, any> | ((values: Record<string, any>) => Record<string, any>);
  confirmMessage?: string;
  successMessage?: string;
};

export default defineComponent({
  name: "ActionButtonsInput",
  props: {
    input: { type: Object as PropType<{ label: string; buttons: ActionButton[] }>, required: true },
    values: { type: Object as PropType<Record<string, any>>, required: true },
    id: { type: [String, Number], required: true },
  },
  setup(props) {
    const loadingKey = ref<string | number | null>(null);

    const resolveEndpoint = (btn: ActionButton) => {
      if (!btn.endpoint) return "";
      if (typeof btn.endpoint === "function") {
        return btn.endpoint(props.values, { id: props.id });
      }
      let url = btn.endpoint;
      if (props.id) url = url.replace("{id}", String(props.id));
      if (props.values?.providerId) url = url.replace("{providerId}", String(props.values.providerId));
      return url;
    };

    const resolveBody = (btn: ActionButton) => {
      if (!btn.body) return {};
      return typeof btn.body === "function" ? btn.body(props.values) : btn.body;
    };

    const callEndpoint = async (btn: ActionButton, key: string | number) => {
      const endpoint = resolveEndpoint(btn);
      if (!endpoint) return;

      if (btn.confirmMessage && !window.confirm(btn.confirmMessage)) return;

      loadingKey.value = key;
      try {
        const method = (btn.method || "GET").toUpperCase();
        switch (method) {
          case "POST":
            await ApiService.post(endpoint, resolveBody(btn));
            break;
          case "PATCH":
            await ApiService.patch(endpoint, resolveBody(btn));
            break;
          case "DELETE":
            await ApiService.delete(endpoint);
            break;
          default:
            await ApiService.get(endpoint);
            break;
        }
        Messages.showSuccess(btn.successMessage || "Action executed successfully");
      } catch (err: any) {
        Messages.showError({ action: [err?.response?.data?.message || "Action failed"] });
      } finally {
        loadingKey.value = null;
      }
    };

    return { callEndpoint, loadingKey };
  },
});
